import React, { useState, useEffect } from 'react';
import './DraftTutorial.css';

/* ─── Shared K logo ─────────────────────────────────────────────────────────── */

const K_LOGO_ICON = (
  <img src="/kickopp-k-logo.svg" alt="Kickopp"
       style={{ width: '56px', height: '56px', objectFit: 'contain' }} />
);

/* ─── Illustrations ──────────────────────────────────────────────────────────── */

/* Horizontal card matching real game board layout */
function HCard({ name, pos, posCol, att, def, flag, mirrorStats, highlight, eliminated }) {
  const photoBox = (
    <div style={{
      flex: 1, minHeight: '52px', borderRadius: '6px',
      background: `${posCol}16`, border: `1px solid ${posCol}28`,
    }} />
  );
  const statsCol = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
      <div style={{
        background: '#1d4ed8', color: '#fff', fontWeight: 900, fontSize: '15px',
        borderRadius: '6px', textAlign: 'center', padding: '4px 10px', minWidth: '32px',
      }}>{att}</div>
      <div style={{
        background: '#b91c1c', color: '#fff', fontWeight: 900, fontSize: '15px',
        borderRadius: '6px', textAlign: 'center', padding: '4px 10px',
      }}>{def}</div>
    </div>
  );
  return (
    <div style={{
      width: '170px', borderRadius: '10px',
      border: `2px solid ${highlight ? '#22c55e' : eliminated ? '#ef444466' : posCol + '55'}`,
      background: 'linear-gradient(135deg,#1e293b,#0f172a)',
      padding: '8px', display: 'flex', flexDirection: 'column', gap: '5px',
      boxShadow: highlight ? '0 0 20px rgba(34,197,94,0.3)' : 'none',
      opacity: eliminated ? 0.38 : 1,
      transition: 'opacity 0.2s',
    }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>
        {mirrorStats ? <>{statsCol}{photoBox}</> : <>{photoBox}{statsCol}</>}
      </div>
      <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, textAlign: 'center' }}>{name}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{
          background: posCol, color: '#fff', fontWeight: 900,
          fontSize: '10px', padding: '2px 7px', borderRadius: '5px',
        }}>{pos}</div>
        <span style={{ fontSize: '13px' }}>{flag}</span>
      </div>
    </div>
  );
}

function TurnIllustration() {
  // Attacker ATT 14 > Defender DEF 11 → successful kickopp
  return (
    <div className="dt-visual" style={{ justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
      {/* Attacker — P1 style: photo left, stats right */}
      <HCard
        name="K. Simpson" pos="CM" posCol="#10b981" att={14} def={9}
        flag="🇯🇲" mirrorStats={false} highlight={true}
      />

      {/* Green arrow — successful kickopp */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
        <div style={{
          color: '#22c55e', fontSize: '32px', lineHeight: 1,
          filter: 'drop-shadow(0 0 10px #22c55e)',
        }}>→</div>
        <div style={{
          color: '#22c55e', fontSize: '9px', fontWeight: 900,
          letterSpacing: '0.5px', textAlign: 'center',
        }}>KICKOPP!</div>
        <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600 }}>14 ATT &gt; 11 DEF</div>
      </div>

      {/* Defender — P2 style: stats left, photo right; eliminated */}
      <HCard
        name="Arturo" pos="AM" posCol="#f97316" att={10} def={11}
        flag="🇪🇸" mirrorStats={true} eliminated={true}
      />
    </div>
  );
}

function ActionPointsIllustration() {
  return (
    <div className="dt-visual" style={{ justifyContent: 'center', alignItems: 'center' }}>
      <img
        src="/actions-grid.png"
        alt="Actions grid showing AP costs"
        style={{ maxWidth: '100%', borderRadius: '10px', display: 'block' }}
      />
    </div>
  );
}

function KPawnIllustration() {
  // Show 3 cards with K1/K2/K3 badges (inactive) + 1 fresh card (active)
  const kCards = [
    { name: 'A. Boban',   pos: 'CM',  posCol: '#10b981', badge: 'K1', oldest: true  },
    { name: 'K. Simpson', pos: 'CM',  posCol: '#10b981', badge: 'K2', oldest: false },
    { name: 'T. Moran',   pos: 'DCB', posCol: '#ef4444', badge: 'K3', oldest: false },
  ];
  return (
    <div className="dt-visual" style={{ flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
      {/* Top: 3 cards with K badges — cannot Kickopp */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        {kCards.map((c, i) => (
          <div key={i} style={{
            width: '110px', borderRadius: '10px',
            border: `1.5px solid ${c.oldest ? '#ef444488' : c.posCol + '55'}`,
            background: 'linear-gradient(135deg,#1e293b,#0f172a)',
            padding: '8px 7px', display: 'flex', flexDirection: 'column', gap: '5px',
            opacity: c.oldest ? 0.5 : 0.85, position: 'relative',
          }}>
            {/* K badge top-right */}
            <div style={{
              position: 'absolute', top: '-10px', right: '-8px',
              background: c.oldest ? '#ef4444' : '#eab308',
              color: '#000', fontWeight: 900, fontSize: '10px',
              padding: '2px 6px', borderRadius: '6px',
              border: '2px solid rgba(4,8,20,0.9)',
            }}>{c.badge}</div>
            <div style={{ width: '100%', height: '34px', borderRadius: '5px', background: c.posCol + '14', border: `1px solid ${c.posCol}22` }} />
            <div style={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textAlign: 'center' }}>{c.name}</div>
            <div style={{ background: c.posCol, color: '#fff', fontWeight: 900, fontSize: '9px', padding: '2px 5px', borderRadius: '4px', alignSelf: 'flex-start' }}>{c.pos}</div>
            {/* Cannot attack label */}
            <div style={{ color: '#ef4444', fontSize: '8px', fontWeight: 800, textAlign: 'center', letterSpacing: '0.3px' }}>
              {c.oldest ? '🔄 freed by FIFO' : '🚫 cannot Kickopp'}
            </div>
          </div>
        ))}
      </div>

      {/* FIFO explanation row */}
      <div style={{
        background: 'rgba(4,8,20,0.6)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '10px',
        padding: '8px 14px',
        display: 'flex', gap: '10px', alignItems: 'center',
        maxWidth: '360px', width: '100%',
      }}>
        <div style={{ fontSize: '18px', flexShrink: 0 }}>♟</div>
        <div style={{ color: '#94a3b8', fontSize: '11px', lineHeight: 1.4 }}>
          When a 4th K Pawn is earned, the <strong style={{ color: '#ef4444' }}>oldest (K1)</strong> is removed —
          that card becomes <strong style={{ color: '#22c55e' }}>active again</strong> and the new K Pawn is assigned to the attacker.
        </div>
      </div>
    </div>
  );
}

function WinningIllustration() {
  return (
    <div className="dt-visual" style={{ justifyContent: 'center', gap: '24px', alignItems: 'stretch' }}>
      {[
        {
          title: '⚔️ Elimination',
          col: '#ef4444',
          desc: 'When your ATT exceeds the opponent\'s DEF — their card is eliminated from the board.',
        },
        {
          title: '©️ Captain Value',
          col: '#eab308',
          desc: 'The Captain survives one extra attack and counts as 2 cards on the final score — protecting or eliminating them matters more.',
        },
        {
          title: '🏆 Score',
          col: '#10b981',
          desc: 'The player with the most remaining cards after 20 turns each wins the match.',
        },
      ].map((w, i) => (
        <div key={i} style={{
          background: 'rgba(4,8,20,0.55)',
          border: `1.5px solid ${w.col}44`,
          borderRadius: '12px',
          padding: '12px 14px',
          flex: 1,
          minWidth: 0,
        }}>
          <div style={{ color: w.col, fontWeight: 900, fontSize: '13px', marginBottom: '6px' }}>{w.title}</div>
          <div style={{ color: '#94a3b8', fontSize: '12px', lineHeight: 1.4 }}>{w.desc}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Step definitions ───────────────────────────────────────────────────────── */

const STEPS = [
  {
    id: 'welcome',
    icon: K_LOGO_ICON,
    accent: '#D4A017',
    badge: 'MATCH GUIDE',
    title: 'The Match Begins!',
    body: <>
      You and your opponent take turns and choose actions with the main goal of{' '}
      <strong style={{ color: '#D4A017' }}>eliminating</strong> each other's cards.
      Every action — Kickopp, Upgrade, Move, Add or Buy Boosts, or Action Cards — costs{' '}
      <strong style={{ color: '#D4A017' }}>Action Points (AP)</strong>.
    </>,
    Visual: null,
    hint: null,
  },
  {
    id: 'turns',
    icon: K_LOGO_ICON,
    accent: '#60a5fa',
    badge: '1/4 · TURNS & COMBAT',
    title: 'Taking Turns',
    body: <>
      On <strong style={{ color: '#60a5fa' }}>your turn</strong>, select one of your available actions.{' '}
      <strong style={{ color: '#60a5fa' }}>Kickopp</strong> is the main action — use it to attack the opponent's defence.
      <br />
      If your <strong style={{ color: '#60a5fa' }}>ATT</strong> beats the{' '}
      <strong style={{ color: '#f87171' }}>DEF</strong> of the card placed in front of yours —
      it's a successful Kickopp and that card is <strong style={{ color: '#ef4444' }}>eliminated</strong> (unless it's the Captain).
    </>,
    Visual: TurnIllustration,
    hint: '📍 Right-click your attacking card and drag it onto the opponent\'s card to Kickopp. A green arrow means success!',
  },
  {
    id: 'ap',
    icon: K_LOGO_ICON,
    accent: '#eab308',
    badge: '2/4 · ACTION POINTS',
    title: 'Action Points',
    body: <>
      Each turn you start with a pool of <strong style={{ color: '#eab308' }}>Action Points (AP)</strong>,
      starting with <strong style={{ color: '#eab308' }}>1</strong> in the first turn and increasing{' '}
      <strong style={{ color: '#eab308' }}>+1</strong> after each Upgrade.
      You can spend them across <strong style={{ color: '#eab308' }}>Kickopp</strong> (attacking opponent's cards),{' '}
      <strong style={{ color: '#eab308' }}>Upgrade</strong>, Cards Move,
      Adding or Buying ATT &amp; DEF Boosts, Substitution, or execute <strong style={{ color: '#eab308' }}>Action Cards</strong>.
      Unused AP is lost at the end of your turn!
    </>,
    Visual: ActionPointsIllustration,
    hint: '📍 Your current AP is shown in the Action Points Counter and it updates while you spend the points',
  },
  {
    id: 'kpawn',
    icon: K_LOGO_ICON,
    accent: '#a855f7',
    badge: '3/4 · KICKOPP PAWN',
    title: 'The Kickopp Pawn',
    body: <>
      When a card successfully <strong style={{ color: '#a855f7' }}>eliminates</strong> an opponent with Kickopp,
      it earns a <strong style={{ color: '#a855f7' }}>K Pawn</strong> badge (K1, K2, K3).
      A card with a <strong style={{ color: '#a855f7' }}>K Pawn</strong> <strong style={{ color: '#ef4444' }}>cannot Action Kickopp</strong> —
      it can still defend, move, be substituted, or receive boosts.
      <br /><br />
      Each player can hold a maximum of <strong style={{ color: '#a855f7' }}>3 K Pawns</strong> at once.
      When a 4th is earned, the card that earned its <strong style={{ color: '#eab308' }}>K Pawn first</strong> loses its badge
      and becomes active for Kickopp again.
    </>,
    Visual: KPawnIllustration,
    hint: '📍 Watch the K1 K2 K3 badges on your cards to know which ones can still Kickopp',
  },
  {
    id: 'winning',
    icon: K_LOGO_ICON,
    accent: '#10b981',
    badge: '4/4 · WINNING',
    title: 'How to Win',
    body: <>
      <strong style={{ color: '#ef4444' }}>Eliminate</strong> more opponent cards than your opponent.
      The manager with the highest number of cards after{' '}
      <strong style={{ color: '#10b981' }}>20 turns each</strong> wins the match.
      <br /><br />
      If both managers end up with the same number of cards after 20 turns —
      the match finishes as a <strong style={{ color: '#94a3b8' }}>Draw</strong>.
      <br /><br />
      Remember — the <strong style={{ color: '#eab308' }}>Captain</strong> counts as{' '}
      <strong style={{ color: '#eab308' }}>2 cards</strong> on the score if still having the C badge.
    </>,
    Visual: null,
    hint: '📍 Watch the turn counter at the top — 20 turns each before final score',
  },
];

/* ─── Main component ─────────────────────────────────────────────────────────── */

export default function BattleTutorial({ onComplete, onSkip, isVertical = false }) {
  const [step, setStep]       = useState(0);
  const [animDir, setAnimDir] = useState('in');

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast  = step === STEPS.length - 1;

  const navigate = (delta) => {
    setAnimDir(delta > 0 ? 'out-left' : 'out-right');
    setTimeout(() => {
      setStep(s => s + delta);
      setAnimDir('in');
    }, 200);
  };

  const handleNext = () => isLast ? onComplete() : navigate(1);
  const handleBack = () => navigate(-1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft' && !isFirst) handleBack();
      if (e.key === 'Escape') onSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const Visual = current.Visual;

  return (
    <div className="dt">
      <div className="dt__overlay" />
      <div
        className={`dt__card dt__card--${animDir}`}
        style={{ '--accent': current.accent }}
        onClick={e => e.stopPropagation()}
      >
        <button className="dt__close" onClick={onSkip} title="Close (Esc)">✕</button>
        <div className="dt__accent-bar" />

        <div className="dt__header">
          <div className="dt__icon-wrap">
            <span className="dt__icon">{current.icon}</span>
          </div>
          <div className="dt__header-text">
            <div className="dt__badge">{current.badge}</div>
            <h2 className="dt__title">{current.title}</h2>
          </div>
        </div>

        <p className="dt__body">{current.body}</p>

        {current.hint && (
          <div className="dt__location-hint">
            {typeof current.hint === 'function' ? current.hint(isVertical) : current.hint}
          </div>
        )}

        {Visual && (
          <div className="dt__visual-wrap">
            <Visual />
          </div>
        )}

        <div className="dt__footer">
          <div className="dt__dots">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                className={`dt__dot ${i === step ? 'dt__dot--active' : ''} ${i < step ? 'dt__dot--done' : ''}`}
                style={i === step ? { background: current.accent, boxShadow: `0 0 8px ${current.accent}88` } : {}}
                onClick={() => { if (i !== step) { setAnimDir(i > step ? 'out-left' : 'out-right'); setTimeout(() => { setStep(i); setAnimDir('in'); }, 200); } }}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
          <div className="dt__nav">
            {!isFirst && (
              <button className="dt__btn dt__btn--back" onClick={handleBack}>← Back</button>
            )}
            <button
              className="dt__btn dt__btn--next"
              style={{ background: current.accent, boxShadow: `0 6px 20px ${current.accent}55` }}
              onClick={handleNext}
            >
              {isFirst ? "Let's Go →" : isLast ? '🏆 Let\'s Play!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
